import { AppShell, Center, Loader } from '@mantine/core';
import { useUnit } from 'effector-react';
import { Gamepad2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createActivateDeviceModal } from 'features/activate-device';
import { createActivatePromocodeModal } from 'features/activate-promocode';
import { createCancelPromocodeModal } from 'features/cancel-promocode';
import { createCompletePromocodeModal } from 'features/complete-promocode';
import { createDeleteDeviceModal } from 'features/delete-device';
import { createDepositPlayerModal } from 'features/deposit-player';
import { createEditDeviceModal } from 'features/edit-device';
import { createResetPlayerBalance } from 'features/reset-player-balance';
import { createWithdrawPlayerModal } from 'features/withdraw-player';
import { gameAsideModel } from 'entities/game';
import { Empty } from 'shared/ui';
import { gamePageModel } from '../../model';
import { GamePageAccordion } from '../game-page-accordion';
import cls from './game-page.module.scss';

const ActivateDeviceModal = createActivateDeviceModal(gamePageModel.activateDeviceModel);

const ActivatePromocodeModal = createActivatePromocodeModal(
	gamePageModel.activatePromocodeModel,
	gamePageModel.cancelPromocodeModel,
);

const CancelPromocodeModal = createCancelPromocodeModal(gamePageModel.cancelPromocodeModel);

const CompletePromocodeModal = createCompletePromocodeModal(gamePageModel.completePromocodeModel);

const EditDeviceModal = createEditDeviceModal(gamePageModel.editDeviceModel);

const DeleteDeviceModal = createDeleteDeviceModal(gamePageModel.deleteDeviceModel);

const DepositPlayerModal = createDepositPlayerModal(gamePageModel.depositPlayerModel);

const WithdrawPlayerModal = createWithdrawPlayerModal(gamePageModel.withdrawPlayerModel);

const ResetPlayerBalanceModal = createResetPlayerBalance(gamePageModel.resetBalancePlayerModel);

export const GamePage = () => {
	const { t } = useTranslation('common');

	const [gameName, pending, isEmptyGames] = useUnit([
		gameAsideModel.$gameName,
		gameAsideModel.$pending,
		gamePageModel.$isEmptyGames,
	]);

	if (pending)
		return (
			<AppShell.Main className={cls.gamePage}>
				<Center>
					<Loader />
				</Center>
			</AppShell.Main>
		);

	if (isEmptyGames)
		return (
			<AppShell.Main
				data-empty
				className={cls.gamePage}
			>
				<Empty
					icon={<Gamepad2 />}
					title={t('game-page.empty-games.title')}
					message={t('game-page.empty-games.message')}
				/>
			</AppShell.Main>
		);

	return (
		<>
			<AppShell.Main className={cls.gamePage}>
				<h1 className={cls.gamePageTitle}>
					{gameName} {t('game-page.devices')}
				</h1>
				<ActivateDeviceModal.Button />
				<GamePageAccordion
					deviceActionSlot={(device) => (
						<>
							<EditDeviceModal.ActionIcon
								modalData={device}
								onClick={(e) => e.stopPropagation()}
							/>
							<DeleteDeviceModal.ActionIcon
								modalData={device}
								onClick={(e) => e.stopPropagation()}
							/>
						</>
					)}
					playerActionSlot={(player) => (
						<>
							{player.voucherItem === null ? (
								<>
									<DepositPlayerModal.Button modalData={player} />
									<WithdrawPlayerModal.Button
										modalData={player}
										disabled={player.balance <= 0}
									/>
									<ResetPlayerBalanceModal.Button
										modalData={player}
										disabled={player.balance <= 0}
									/>
								</>
							) : (
								<CompletePromocodeModal.Button modalData={player} />
							)}
							<ActivatePromocodeModal.Button modalData={player} />
						</>
					)}
				/>
			</AppShell.Main>
			<EditDeviceModal />
			<DeleteDeviceModal
				classNames={{
					body: cls.gamePageDeleteModalBody,
				}}
			/>
			<ActivateDeviceModal />
			<ActivatePromocodeModal />
			<CancelPromocodeModal />
			<CompletePromocodeModal />
			<DepositPlayerModal />
			<WithdrawPlayerModal />
			<ResetPlayerBalanceModal />
		</>
	);
};
